import { apply, celebrate, onceToday } from '../core/effects';
import { S } from '../core/game';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { sayEnglish, stopSpeech } from '../audio/speech';
import { logStory } from '../content/passport';
import { STORIES, story, type StoryId } from '../content/stories';
import { addToDeck } from './english';
import { h } from './dom';
import { openPanel, refreshPanel } from './panel';

/** Stories you can always read; the others are found in the world (photos, comics, trips). */
export const LIBRARY: readonly StoryId[] = ['kite', 'mochi', 'market', 'dragon'];

const st = { id: null as StoryId | null, gist: false, q: -1, score: 0, picked: -1, reading: 0 };

export function storyUnlocked(id: StoryId): boolean {
  return LIBRARY.includes(id) || !!S().forever[`story.${id}`];
}

/** Read every line aloud, one after another. */
function readAll(lines: string[]): void {
  const token = ++st.reading;
  let i = 0;
  const next = () => {
    if (token !== st.reading || i >= lines.length) return;
    sayEnglish(lines[i++], 0.85);
    window.setTimeout(next, 900 + lines[i - 1].length * 55);
  };
  next();
}

function reader(): (HTMLElement | null)[] {
  const t = m().world3.stories;
  const s = story(st.id!);
  if (st.q >= 0) return quiz();
  return [
    h('h3', { class: 'story-title' }, `${s.icon} ${s.title}`),
    h(
      'div',
      { class: 'chips' },
      h('button', { class: 'chip small', onclick: () => (sfx('click'), readAll(s.lines)) }, t.readAll),
      h('button', { class: 'chip small', onclick: () => (st.reading++, stopSpeech()) }, t.stop),
      h('button', { class: `chip small${st.gist ? ' on' : ''}`, onclick: () => ((st.gist = !st.gist), refreshPanel('stories')) }, t.gistBtn),
    ),
    st.gist ? h('p', { class: 'story-gist' }, t.gists[s.id]) : null,
    h(
      'div',
      { class: 'story-page' },
      s.lines.map((line) => h('p', null, h('button', { class: 'say', 'aria-label': 'listen', onclick: () => sayEnglish(line, 0.85) }, '🔊'), h('span', null, line))),
    ),
    h('button', { class: 'btn gold wide', onclick: () => ((st.q = 0), (st.score = 0), (st.picked = -1), st.reading++, stopSpeech(), refreshPanel('stories')) }, t.quiz),
  ];
}

function quiz(): (HTMLElement | null)[] {
  const t = m().world3.stories;
  const s = story(st.id!);
  if (st.q >= s.quiz.length) {
    return [
      h('div', { class: 'story-result' }, h('b', null, `${s.icon} ${t.result(st.score, s.quiz.length)}`), h('span', null, '⭐'.repeat(Math.max(1, st.score)))),
      h('div', { class: 'chips' }, h('button', { class: 'btn', onclick: () => ((st.q = -1), refreshPanel('stories')) }, t.again), h('button', { class: 'btn gold', onclick: () => ((st.id = null), refreshPanel('stories')) }, t.list)),
    ];
  }
  const q = s.quiz[st.q];
  return [
    h('p', { class: 'hint' }, t.question(st.q + 1, s.quiz.length)),
    h('p', { class: 'story-q' }, h('button', { class: 'say', onclick: () => sayEnglish(q.q, 0.85) }, '🔊'), h('b', null, q.q)),
    h(
      'div',
      { class: 'talk-options' },
      q.options.map((o, i) =>
        h(
          'button',
          {
            class: `btn talk-opt${st.picked === i ? (i === q.answer ? ' ok' : ' bad') : ''}`,
            onclick: () => {
              sayEnglish(o, 0.9);
              if (i === q.answer) {
                if (st.picked === -1) st.score++;
                sfx('ok');
                window.setTimeout(() => {
                  st.q++;
                  st.picked = -1;
                  if (st.q >= s.quiz.length) finish();
                  refreshPanel('stories');
                }, 650);
              } else sfx('bad');
              st.picked = i;
              refreshPanel('stories');
            },
          },
          o,
        ),
      ),
    ),
    st.picked >= 0 && st.picked !== q.answer ? h('p', { class: 'hint bad' }, t.wrong) : null,
  ];
}

function finish(): void {
  const s = story(st.id!);
  logStory(s.id, st.score);
  for (const w of s.words) addToDeck(w);
  if (onceToday(`story.${s.id}`)) {
    celebrate(st.score === s.quiz.length);
    apply({ stars: 2 + st.score, study: 3 });
  }
}

function list(embedded = false): HTMLElement[] {
  const t = m().world3.stories;
  const e = S().english;
  return [
    h('p', { class: 'hint' }, t.pick),
    h(
      'div',
      { class: 'story-list' },
      STORIES.map((s) => {
        const open = storyUnlocked(s.id);
        return h(
          'button',
          {
            class: `story-card${open ? '' : ' locked'}`,
            disabled: !open,
            onclick: () => (sfx('click'), embedded ? openStory(s.id) : ((st.id = s.id), (st.q = -1), (st.gist = false), refreshPanel('stories'))),
          },
          h('span', { class: 'ico' }, open ? s.icon : '🔒'),
          h('b', null, open ? s.title : t.locked),
          e.stories[s.id] !== undefined ? h('small', null, `${t.read} · ${'★'.repeat(e.stories[s.id])}`) : null,
        );
      }),
    ),
  ];
}

/** Open the story shelf, or one story straight away (found in the world). */
export function openStory(id?: StoryId): void {
  if (id) {
    S().forever[`story.${id}`] = true;
    st.id = id;
    st.q = -1;
    st.gist = false;
  } else st.id = null;
  openPanel(
    'stories',
    () => ({
      title: m().world3.stories.title,
      className: 'stories',
      body: st.id ? reader() : list(),
    }),
    () => {
      st.reading++;
      stopSpeech();
    },
  );
}

/** The same shelf, embedded in the English corner's "Read" tab. */
export function storiesTab(): HTMLElement[] {
  return list(true);
}
